/* Asset: djuce-quantity-selector.js */
class DjuceQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.basePrice = parseInt(this.getAttribute('data-base-price'), 10) || 0;
    this.moneyFormat = this.getAttribute('data-money-format') || "${{amount}}";
    this.cartCount = parseInt(this.getAttribute('data-cart-count'), 10) || 0;

    this.options = this.querySelectorAll('.dqs-option');
    this.hiddenInput = this.querySelector('.dqs-hidden-input');
    
    // Cache the form so we don't bleed into other products on the same page
    this.parentForm = this.closest('form');
    
    // Parse localized strings safely
    try {
      const localesScript = this.querySelector('.dqs-locales-json');
      this.locales = JSON.parse(localesScript.textContent);
    } catch (e) {
      console.error("DQS: Failed to parse locales, using fallbacks.");
      this.locales = { 
        option_1: "4 Glasses", 
        option_2: "8 Glasses (2 Tubes)",
        option_3: "12 Glasses (3 Tubes)",
        goal: "Goal: 3 items (20% Off)",
        max_unlocked: "Max Discount Unlocked!",
        off_10: "10% OFF",
        off_20: "20% OFF",
        standard_shipping: "STANDARD SHIPPING",
        free_shipping: "FREE SHIPPING",
        subtitle_0: "Standard Price",
        subtitle_1: "Free shipping &middot; 10% off order",
        subtitle_2: "Free shipping &middot; 20% off order"
      };
    }

    // Cache internal elements for performance
    this.options.forEach(option => {
      option.priceEl = option.querySelector('.dqs-calc-price');
      option.subtitleEl = option.querySelector('.dqs-subtitle');
      option.qtyValue = parseInt(option.getAttribute('data-qty'), 10);
    });
  }

  async connectedCallback() {
    this.renderPrices();
    this.bindEvents();
    await this.fetchCartState();

    this.ampUpdateHandler = this.handleAmpCartUpdate.bind(this);
    window.addEventListener('djuce:cart:updated', this.ampUpdateHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('djuce:cart:updated', this.ampUpdateHandler);
  }

  async fetchCartState() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) throw new Error('Network response failed');
      const cart = await response.json();
      this.cartCount = cart.item_count || 0;
      this.renderPrices();
    } catch (error) {
      console.warn('DQS: Cart fetch failed. Defaulting to Liquid cart count.', error);
    }
  }

  handleAmpCartUpdate(event) {
    const cart = event.detail;
    if (cart && typeof cart.item_count !== 'undefined') {
      this.cartCount = cart.item_count;
      this.renderPrices();
    }
  }

  getDiscountData(totalFutureQty) {
    if (totalFutureQty >= 3) return { multiplier: 0.8, text: this.locales.subtitle_2 };
    if (totalFutureQty === 2) return { multiplier: 0.9, text: this.locales.subtitle_1 };
    return { multiplier: 1, text: this.locales.subtitle_0 };
  }

  formatMoney(cents) {
    if (isNaN(cents)) return "";
    const floatValue = cents / 100;
    const roundedValue = Math.ceil(floatValue - 0.5);
    const valueWithDecimals = roundedValue.toFixed(2);

    // Group the integer part with a space every 3 digits, comma as the
    // decimal mark (SEK/space-separator currencies, e.g. "1 234,56 kr")
    const spaceSeparated = (str) => {
      const [intPart, decPart] = str.toString().split('.');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return decPart != null ? `${grouped},${decPart}` : grouped;
    };

    // Added 'g' flag for global replacement safety
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, spaceSeparated(roundedValue))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, spaceSeparated(valueWithDecimals))
      .replace(/\{\{\s*amount\s*\}\}/g, valueWithDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, valueWithDecimals.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, roundedValue);
  }

  renderPrices() {
    this.options.forEach(option => {
      const projectedTotalCartQty = this.cartCount + option.qtyValue;
      const discount = this.getDiscountData(projectedTotalCartQty);

      const originalTotalPrice = this.basePrice * option.qtyValue;
      const totalPrice = originalTotalPrice * discount.multiplier;

      if (option.priceEl) {
        let priceHTML = `<span>${this.formatMoney(totalPrice)}</span>`;
        if (discount.multiplier < 1) {
          priceHTML += ` <span class="dqs-price-compare">${this.formatMoney(originalTotalPrice)}</span>`;
        }
        option.priceEl.innerHTML = priceHTML;
      }

      if (option.subtitleEl) {
        option.subtitleEl.innerHTML = discount.text;
        if (discount.multiplier < 1) {
          option.subtitleEl.style.color = "#378d66"; 
        } else {
          option.subtitleEl.style.color = "#737373"; 
        }
      }
    });
  }

  bindEvents() {
    this.options.forEach(option => {
      const selectOption = () => {
        this.options.forEach(opt => {
          opt.classList.remove('active');
          opt.setAttribute('aria-checked', 'false'); 
        });

        option.classList.add('active');
        option.setAttribute('aria-checked', 'true'); 

        const selectedQty = option.getAttribute('data-qty');
        if (this.hiddenInput) this.hiddenInput.value = selectedQty;

        // Scoped to the parent form to prevent cross-product pollution
        if (this.parentForm) {
          const themeQtyInput = this.parentForm.querySelector('input[name="quantity"]:not(.dqs-hidden-input)');
          if (themeQtyInput) themeQtyInput.value = selectedQty;
        }
      };

      option.addEventListener('click', selectOption);
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); 
          selectOption();
        }
      });
    });
  }
}

if (!customElements.get('djuce-quantity-selector')) {
  customElements.define('djuce-quantity-selector', DjuceQuantitySelector);
}

